import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import {
  FiType, FiImage, FiX, FiCheck, FiUploadCloud,
  FiTrash2, FiAlertCircle, FiFolder
} from 'react-icons/fi';
import supabase from '../../supabase/supabase';

const LogoPopup = ({ onClose, onUpdate, onSaveImmediate, currentData, onOpenLibrary }) => {
  const [tempText, setTempText] = useState(currentData.content);
  const [tempUrl, setTempUrl] = useState(currentData.url);
  const [logoType, setLogoType] = useState(currentData.type);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadError('');
    if (!file.type.startsWith('image/')) {
      setUploadError('Invalid file type. Please upload a PNG, JPG, SVG, or WebP.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File too large. Maximum size is 5MB.');
      return;
    }
    setIsUploading(true);
    setUploadProgress(20);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `logo-${Date.now()}-${Math.random().toString(36).substring(2, 7)}.${fileExt}`;
      const filePath = `logos/${fileName}`;
      setUploadProgress(40);
      const { error: uploadError } = await supabase.storage
        .from('media')
        .upload(filePath, file, { cacheControl: '3600', upsert: false });
      
      if (uploadError) {
        if (uploadError.message?.toLowerCase().includes('bucket')) {
          throw new Error("Storage bucket 'media' not found. Please create it in Supabase.");
        } else if (uploadError.message?.toLowerCase().includes('policy')) {
          throw new Error('Permission denied. Check your Supabase Storage RLS policies.');
        }
        throw new Error(uploadError.message || 'Upload failed.');
      }
      setUploadProgress(80);
      const { data: publicUrlData } = supabase.storage.from('media').getPublicUrl(filePath);
      if (!publicUrlData?.publicUrl) throw new Error('Failed to get public URL.');
      
      setTempUrl(publicUrlData.publicUrl);
      setUploadProgress(100);
    } catch (err) {
      setUploadError(err.message || 'An unexpected error occurred.');
    } finally {
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
        e.target.value = '';
      }, 400);
    }
  };

  const handleSave = () => {
    const newLogoData = { type: logoType, content: tempText, url: tempUrl };
    onUpdate(newLogoData);
    if (onSaveImmediate) {
      onSaveImmediate(newLogoData);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <div>
            <h3 className="font-black text-gray-900 text-lg">Logo Settings</h3>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Customize your brand</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        {/* Type Toggle */}
        <div className="p-6 pb-0">
          <div className="flex p-1.5 bg-gray-100 rounded-2xl">
            {[
              { id: 'text', icon: FiType, label: 'Text Logo' },
              { id: 'image', icon: FiImage, label: 'Image Logo' },
            ].map((opt) => (
              <button
                key={opt.id}
                onClick={() => { setLogoType(opt.id); setUploadError(''); }}
                className={`flex-1 flex items-center justify-center space-x-2 py-3 rounded-xl text-sm font-black uppercase tracking-wider transition-all ${
                  logoType === opt.id ? 'bg-white shadow-xl text-teal-600' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <SafeIcon icon={opt.icon} className="w-4 h-4" />
                <span>{opt.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <AnimatePresence mode="wait">
            {logoType === 'text' ? (
              <motion.div
                key="text"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
              >
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">
                  Logo Text
                </label>
                <input
                  type="text"
                  value={tempText}
                  onChange={(e) => setTempText(e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-teal-400 transition-all font-black tracking-tighter text-gray-900"
                  placeholder="Enter your brand name..."
                />
                {tempText && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-xl flex items-center justify-center border border-gray-100">
                    <span className="text-2xl font-black text-gray-900 tracking-tighter">{tempText}</span>
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="image"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="space-y-4"
              >
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">
                  Upload Logo File
                </label>
                {uploadError && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-red-50 border border-red-200 text-red-600 rounded-xl p-3 flex items-start space-x-2">
                    <SafeIcon icon={FiAlertCircle} className="w-4 h-4 shrink-0 mt-0.5" />
                    <p className="text-xs font-medium">{uploadError}</p>
                  </motion.div>
                )}
                {tempUrl ? (
                  <div className="relative group rounded-2xl overflow-hidden border-2 border-gray-100 bg-gray-50 p-6 flex items-center justify-center min-h-[140px]">
                    <img src={tempUrl} alt="Logo preview" className="max-h-24 max-w-full object-contain" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-3">
                      <label className="p-3 bg-white text-teal-600 rounded-xl cursor-pointer hover:scale-110 transition-transform shadow-lg">
                        <SafeIcon icon={FiUploadCloud} className="w-5 h-5" />
                        <input type="file" className="hidden" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={handleFileUpload} />
                      </label>
                      {onOpenLibrary && (
                        <button
                          onClick={() => onOpenLibrary((image) => setTempUrl(image.url))}
                          className="p-3 bg-blue-500 text-white rounded-xl hover:scale-110 transition-transform shadow-lg"
                        >
                          <SafeIcon icon={FiFolder} className="w-5 h-5" />
                        </button>
                      )}
                      <button
                        onClick={() => setTempUrl('')}
                        className="p-3 bg-red-500 text-white rounded-xl hover:scale-110 transition-transform shadow-lg"
                      >
                        <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Upload Button */}
                    <label className={`flex flex-col items-center justify-center min-h-[120px] border-2 border-dashed rounded-2xl cursor-pointer transition-all ${isUploading ? 'bg-teal-50 border-teal-300' : 'bg-gray-50 border-gray-200 hover:border-teal-400 hover:bg-teal-50/40'}`}>
                      <div className="flex flex-col items-center space-y-3 px-6 text-center w-full py-6">
                        <div className={`p-4 rounded-2xl transition-all ${isUploading ? 'bg-teal-500 text-white shadow-lg shadow-teal-200/50' : 'bg-white text-gray-300 shadow-sm'}`}>
                          <SafeIcon icon={FiUploadCloud} className={`w-7 h-7 ${isUploading ? 'animate-bounce' : ''}`} />
                        </div>
                        <div>
                          <span className="text-sm font-black text-gray-800 block">
                            {isUploading ? 'Uploading...' : 'Click to upload your logo'}
                          </span>
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1 block">
                            PNG, JPG, SVG or WebP · Max 5MB
                          </span>
                        </div>
                        {isUploading && (
                          <div className="w-full max-w-[180px] bg-teal-100 h-1.5 rounded-full overflow-hidden">
                            <motion.div initial={{ width: 0 }} animate={{ width: `${uploadProgress}%` }} className="bg-teal-500 h-full rounded-full" />
                          </div>
                        )}
                      </div>
                      <input type="file" className="hidden" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={handleFileUpload} disabled={isUploading} />
                    </label>
                    
                    {/* Library Button */}
                    {onOpenLibrary && (
                      <button
                        onClick={() => onOpenLibrary((image) => setTempUrl(image.url))}
                        className="w-full py-4 border-2 border-dashed border-blue-200 bg-blue-50/50 rounded-2xl flex items-center justify-center space-x-3 hover:border-blue-400 hover:bg-blue-50 transition-all"
                      >
                        <SafeIcon icon={FiFolder} className="w-5 h-5 text-blue-500" />
                        <span className="font-bold text-blue-600">Choose from Library</span>
                      </button>
                    )}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="p-6 bg-gray-50 border-t border-gray-100 flex space-x-3">
          <button onClick={onClose} className="flex-1 py-3 font-bold text-gray-500 hover:bg-gray-200 rounded-xl transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isUploading || (logoType === 'image' && !tempUrl)}
            className="flex-1 py-3 bg-teal-500 text-white font-black rounded-xl shadow-lg shadow-teal-100 hover:bg-teal-600 transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <SafeIcon icon={FiCheck} className="w-4 h-4" />
            <span>Save Logo</span>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default LogoPopup;